<template>
    <template v-if="showTypeForm">
        <LazyTypeformEmbed
            :typeform-id="typeformId"
            :hydrate-on-idle="true"
        />
    </template>

    <form
        v-else-if="formConfiguredCorrectly"
        class="enquiry-form"
        autocomplete="off"
        @submit.prevent="submitForm"
    >
        <BaseFormField>
            <BaseFormDropdown :show-icon="true">
                <template #label>
                    <div v-if="selectedProperty">
                        {{ selectedProperty.city?.[0]?.title }} — {{ selectedProperty.area }}
                    </div>
                    <div v-else>
                        {{ $t('enquiry-form.select-property') }}
                    </div>
                </template>

                <template #content="{ toggleDropdown }">
                    <PropertyThumbnails
                        :selected-location="selectedProperty"
                        @update:location="selectedProperty = $event; toggleDropdown()"
                    />
                </template>
            </BaseFormDropdown>
        </BaseFormField>

        <BaseFormField v-if="props.enquiryType === 'companyBooking'">
            <BaseInput
                v-model="numberOfNightsPerYear"
                type="number"
                name="numberOfNightsPerYear"
                min="0"
                required
                :label="$t('enquiry-form.number-of-nights-per-year')"
                :novalidate="false"
            />
        </BaseFormField>

        <BaseFormField
            v-else
            class="enquiry-form__dates"
        >
            <BaseFormDropdown>
                <template #label>
                    <div v-if="checkInDate">
                        {{ format(new Date(checkInDate), 'MMMM d, yyyy') }}
                    </div>
                    <div v-else>
                        {{ $t('enquiry-form.preferred-check-in-date') }}
                    </div>
                </template>

                <template #content>
                    <BaseDatePicker
                        :date="checkInDate"
                        :active-property="selectedProperty"
                        mode="single"
                        @update:date="checkInDate = $event"
                    />
                </template>

                <template
                    v-if="selectedProperty?.bookingsStartDate"
                    #description
                >
                    {{ $t('enquiry-form.bookings-start-date', { date: format(new Date(selectedProperty.bookingsStartDate), 'MMMM d, yyyy') }) }}
                </template>
            </BaseFormDropdown>

            <BaseFormDropdown>
                <template #label>
                    <div v-if="checkOutDate">
                        {{ format(new Date(checkOutDate), 'MMMM d, yyyy') }}
                    </div>
                    <div v-else>
                        {{ $t('enquiry-form.preferred-check-out-date') }}
                    </div>
                </template>

                <template #content>
                    <BaseDatePicker
                        :date="checkOutDate"
                        :active-property="selectedProperty"
                        mode="single"
                        @update:date="checkOutDate = $event"
                    />
                </template>
            </BaseFormDropdown>
        </BaseFormField>

        <BaseFormField v-if="props.enquiryType === 'groupBooking'">
            <BaseInput
                v-model="numberOfGuests"
                type="number"
                name="numberOfGuests"
                min="0"
                required
                :label="$t('enquiry-form.number-of-guests')"
                :novalidate="false"
            />
        </BaseFormField>

        <BaseFormField>
            <BaseInput
                v-model="firstName"
                type="text"
                name="firstName"
                required
                :label="$t('enquiry-form.firstName')"
                :novalidate="false"
            />
        </BaseFormField>

        <BaseFormField>
            <BaseInput
                v-model="lastName"
                type="text"
                name="firstName"
                required
                :label="$t('enquiry-form.lastName')"
                :novalidate="false"
            />
        </BaseFormField>

        <BaseFormField>
            <BaseInput
                v-model="email"
                type="email"
                name="email"
                :label="$t('enquiry-form.email')"
                required
                :novalidate="false"
            />
        </BaseFormField>

        <BaseFormField>
            <BaseInput
                v-model="companyName"
                type="text"
                name="companyName"
                :label="$t('enquiry-form.company-name')"
                :novalidate="false"
            />
        </BaseFormField>

        <BaseFormField>
            <BaseTextarea
                v-model="message"
                type="text"
                name="message"
                rows="5"
                :label="$t('enquiry-form.message')"
                required
                :novalidate="false"
            />
            <div class="enquiry-form__description">
                {{ $t('enquiry-form.message-description') }}
            </div>
        </BaseFormField>

        <BaseFormField>
            <BaseButton
                class="base-button--tertiary"
                type="submit"
                :disabled="submitted || error"
            >
                <template #default>
                    <template v-if="submitted">
                        {{ $t('your-message-has-been-sent') }}
                    </template>
                    <template v-else-if="error">
                        {{ $t('error') }}
                    </template>
                    <template v-else>
                        {{ $t('send') }}
                    </template>

                    <BaseIcon
                        v-show="submitted"
                        icon="check"
                    />
                </template>
            </BaseButton>
        </BaseFormField>
    </form>
</template>

<script setup>
import { format } from 'date-fns';
import { useGtm } from '@gtm-support/vue-gtm';

const props = defineProps({
    typeformId: {
        type: String,
        default: '01HPEKB26EE8A5845W1VVP3F8E',
        required: false
    },
    enquiryType: {
        type: String,
        default: '',
        required: true,
        validator: (value) => ['companyBooking', 'groupBooking', 'longStay'].includes(value)
    },
    postmarkAddress: {
        type: String,
        default: '',
        required: true
    },
    postmarkServerApiToken: {
        type: String,
        default: '',
        required: true
    },
    postmarkStream: {
        type: String,
        default: '',
        required: true
    }
});

const showTypeForm = computed(() => {
    return props.typeformId && props.typeformId.length > 0;
});

const formConfiguredCorrectly = computed(() => {
    if (!props.postmarkAddress || !props.postmarkServerApiToken || !props.postmarkStream) {
        return false;
    } else {
        return true;
    }
});

// Possible fields
const selectedProperty = ref(null);
const checkInDate = ref(null);
const checkOutDate = ref(null);
const numberOfNightsPerYear = ref(null);
const numberOfGuests = ref(null);
const firstName = ref('');
const lastName = ref('');
const email = ref('');
const companyName = ref('');
const message = ref('');

const submitted = ref(false);
const error = ref(false);

async function submitForm() {
    const { status } = await useFetch('/api/enquiry', {
        query: {
            email: email.value || null,
            firstName: firstName.value || null,
            lastName: lastName.value || null,
            message: message.value || null,
            checkInDate: checkInDate.value ? format(new Date(checkInDate.value), 'MMMM d, yyyy') : false || null,
            checkOutDate: checkOutDate.value ? format(new Date(checkInDate.value), 'MMMM d, yyyy') : false || null,
            selectedProperty: selectedProperty.value ? `${selectedProperty.value.city?.[0].title} - ${selectedProperty.value.area}` : false || null,
            numberOfNightsPerYear: numberOfNightsPerYear.value || null,
            numberOfGuests: numberOfGuests.value || null,
            companyName: companyName.value || null,
            uri: window.location.href,
            postmarkAddress: props.postmarkAddress,
            postmarkServerApiToken: props.postmarkServerApiToken,
            postmarkStream: props.postmarkStream
        }
    });

    if (status && status.value === 'success') {
        submitted.value = true;
        error.value = false;

        const gtm = useGtm();
        gtm.trackEvent({
            /* eslint-disable camelcase */
            event: 'enquiry_form_submit',
            /* eslint-enable camelcase */
        });
    } else {
        submitted.value = false;
        error.value = true;
    }
}
</script>

<style lang="less">
.enquiry-form {
    .dn-input.base-input,
    div.dn-textarea,
    .base-form-dropdown__label {
        border-bottom-color: rgba(0, 0, 0, .25);
        line-height: 1;
    }

    .base-form-field.dn-form-field + .base-form-field.dn-form-field {
        margin-top: var(--spacing-lg);
    }
}

.enquiry-form__dates .dn-form-field__wrapper{
    display: flex;
    gap: var(--spacing-lg);
    flex-direction: column;

    @media @q-lg-min {
        flex-direction: row;
    }

    .base-form-dropdown {
        flex: 1;
    }
}

.enquiry-form__description {
    margin-top: .875rem;
    opacity: .5;
    line-height: var(--lineHeight-md);
}
</style>
